// Customer detail (MUI, /customers/:customerId). The design's chrome body:
// breadcrumb + header (avatar/title/status/contact-atoms/Edit+Delete) + 5-card
// KPI strip + tab rail (?tab= URL sync). Progressive data load mirrors the old
// CustomerKnowledgePage; per-tab bodies are separate MUI components.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  getCustomer,
  customerActivity,
  getCustomerBrief,
  listProjects,
  listFiles,
  getCustomerTickets,
  getCustomerSpine,
  saveCustomer,
  deleteCustomerRecord,
  type Customer,
  type Project,
  type StoreObject,
  type CustomerSpine,
  type CustomerTickets,
} from "../../lib/brain-api";
import type { Thought } from "../../lib/brain-types";
import { useProfile } from "../../lib/profile";
import { useAgent, useAgentSnapshot } from "../../agent/AgentProvider";
import { Explain } from "../../agent/Explain";
import { brainTokens } from "../../design/brainTheme";
import { KpiCard, StatusPill, CustomerAvatar, Icon, SectionCard } from "../../mui";
import { CustomerOverviewTabMui } from "./CustomerOverviewTabMui";
import { CustomerFilesTabMui } from "./CustomerFilesTabMui";
import { CustomerProjectsTabMui } from "./CustomerProjectsTabMui";
import { CustomerIntegrationsTabMui } from "./CustomerIntegrationsTabMui";
import { CustomerVisitsTabMui } from "./CustomerVisitsTabMui";
import { CustomerFinanceTabMui } from "./CustomerFinanceTabMui";
import { CustomerTicketsTabMui } from "./CustomerTicketsTabMui";
import { CustomerRoadmapTabMui } from "./CustomerRoadmapTabMui";
import { CustomerEngagementTabMui } from "./CustomerEngagementTabMui";
import { CustomerLogTabMui } from "./CustomerLogTabMui";
import { ScopeDocsTab } from "./docs/ScopeDocsTab";

const c = brainTokens.color;

type TabKey = "overview" | "docs" | "files" | "projects" | "engagement" | "log" | "integrations" | "visits" | "tickets" | "roadmap" | "finance";

export function CustomerKnowledgePageMui() {
  const { customerId = "" } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { profile } = useProfile();
  const { setPageContext } = useAgent();
  const isAdmin = profile?.role === "admin";
  const canSeeFinance = isAdmin || (profile?.departments?.includes("finance") ?? false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [draft, setDraft] = useState<Customer | null>(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState<Thought[]>([]);
  const [brief, setBrief] = useState<Awaited<ReturnType<typeof getCustomerBrief>> | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [files, setFiles] = useState<StoreObject[]>([]);
  const [tickets, setTickets] = useState<CustomerTickets | null>(null);
  const [spine, setSpine] = useState<CustomerSpine | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cust = await getCustomer(customerId);
      setCustomer(cust);
      setDraft(cust);
    } catch {
      enqueueSnackbar("Failed to load customer.", { variant: "error" });
    } finally {
      setLoading(false);
    }
    void customerActivity(customerId, 500).then(setActivity).catch(() => {});
    void getCustomerBrief(customerId).then(setBrief).catch(() => {});
    void listProjects(customerId).then(setProjects).catch(() => {});
    void listFiles(`customers/${customerId}/`).then(setFiles).catch(() => {});
    void getCustomerTickets(customerId).then(setTickets).catch(() => {});
    void getCustomerSpine(customerId).then(setSpine).catch(() => {});
  }, [customerId, enqueueSnackbar]);
  useEffect(() => {
    void load();
  }, [load]);

  // ?tab= drives the active tab; finance hidden when not permitted.
  const requestedTab = (searchParams.get("tab") ?? "overview") as TabKey;
  const activeTab: TabKey = requestedTab === "finance" && !canSeeFinance ? "overview" : requestedTab;

  const goTab = (tab: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };

  // Follow the widget on customer/tab change (agent partner contract).
  useEffect(() => {
    if (!customer) return;
    setPageContext({
      route: `/customers/${customer.customerId}`,
      entity: { type: "customer", id: customer.customerId, name: customer.name },
      tab: activeTab,
    });
    return () => setPageContext(null);
  }, [customer, activeTab, setPageContext]);

  // What's on screen (S1) — ids + numbers the agent can ground on, capped small.
  useAgentSnapshot(
    customer
      ? {
          tab: activeTab,
          status: customer.status,
          projects: projects.slice(0, 10).map((p) => ({ id: p.projectId, name: p.name, status: p.status })),
          ticketCounts: tickets?.counts,
          openTickets: (tickets?.tickets ?? [])
            .filter((t) => t.statusCategory !== "done")
            .slice(0, 10)
            .map((t) => ({ key: t.key, summary: t.summary, status: t.status })),
          fileCount: files.length,
        }
      : null,
  );

  const atoms = activity.length >= 500 ? "500+" : String(activity.length);
  const ticketCount = tickets ? tickets.tickets.length : null;

  const onSaveRecord = async () => {
    if (!draft) return;
    try {
      const saved = await saveCustomer(draft);
      setCustomer(saved);
      setDraft(saved);
      setEditing(false);
      enqueueSnackbar("Record saved.", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Save failed.", { variant: "error" });
    }
  };

  const onDelete = async () => {
    try {
      await deleteCustomerRecord(customerId);
      enqueueSnackbar("Customer deleted.", { variant: "success" });
      navigate("/customers");
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Delete failed.", { variant: "error" });
    }
  };

  const tabDefs = useMemo(() => {
    const defs: { key: TabKey; label: string }[] = [
      { key: "overview", label: "Overview" },
      { key: "docs", label: "Docs" },
      { key: "files", label: `Files${files.length ? ` (${files.length})` : ""}` },
      { key: "projects", label: `Projects${projects.length ? ` (${projects.length})` : ""}` },
      { key: "engagement", label: "Engagement" },
      { key: "log", label: "Log" },
      { key: "integrations", label: "Integrations" },
      { key: "visits", label: "Site Visits" },
      { key: "tickets", label: "Tickets" },
      { key: "roadmap", label: "Roadmap" },
    ];
    if (canSeeFinance) defs.push({ key: "finance", label: "Finance" });
    return defs;
  }, [files.length, projects.length, canSeeFinance]);

  if (loading && !customer) {
    return <Box sx={{ color: c.muted2, fontSize: 14, py: 6 }}>Loading customer…</Box>;
  }
  if (!customer) {
    return (
      <SectionCard title="Customer not found">
        <Box sx={{ fontSize: 13.5, color: c.muted2 }}>No record for “{customerId}”.</Box>
      </SectionCard>
    );
  }

  return (
    <Box>
      {/* breadcrumb */}
      <Box sx={{ fontSize: 13, color: c.muted2, mb: "16px" }}>
        <Box component={RouterLink} to="/customers" sx={{ color: c.muted2, textDecoration: "none", "&:hover": { color: c.ink } }}>
          Customers
        </Box>
        <Box component="span" sx={{ color: c.placeholder, mx: "4px" }}>
          ›
        </Box>
        <Box component="span" sx={{ color: c.ink4 }}>
          {customer.customerId}
        </Box>
      </Box>

      {/* customer header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <CustomerAvatar name={customer.name} size={58} />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <Box component="h1" sx={{ m: 0, fontSize: 36, fontWeight: 700, letterSpacing: "-.02em", lineHeight: 1 }}>
                {customer.name}
              </Box>
              <StatusPill status={customer.status} label={customer.status} dot />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mt: "11px", flexWrap: "wrap" }}>
              <Box sx={{ fontSize: 14, color: c.muted2 }}>
                Primary contact{" "}
                <Box component="span" sx={{ color: c.ink2, fontWeight: 600 }}>
                  {customer.primaryContact ?? "—"}
                </Box>
              </Box>
              <Box sx={{ width: 3, height: 3, borderRadius: "50%", background: "#cdd2d7" }} />
              <Box sx={{ fontSize: 14, color: c.muted2 }}>
                <Box component="span" sx={{ color: c.ink2, fontWeight: 600 }}>
                  {atoms}
                </Box>{" "}
                knowledge atoms
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: "10px", flex: "none" }}>
          <Button variant="outlined" onClick={() => setEditing((v) => !v)} startIcon={<Icon name="edit" size={15} stroke={1.8} />}>
            {editing ? "Editing…" : "Edit record"}
          </Button>
          {isAdmin && (
            <Button
              variant="outlined"
              onClick={() => setDeleteOpen(true)}
              startIcon={<Icon name="trash" size={15} stroke={1.8} />}
              sx={{ color: c.dangerText2, borderColor: c.dangerBorder, "&:hover": { background: "#fdf4f4", borderColor: c.dangerBorder } }}
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>

      {/* KPI strip — completeness / atoms / tickets are Explain-wrapped (P2). */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "14px", mb: "26px" }}>
        <KpiCard
          label="Completeness"
          value={
            <Explain snapshot={{ label: "Completeness", value: customer.completeness ?? 0, unit: "%" }}>
              {customer.completeness == null ? "—" : Math.round(customer.completeness)}
            </Explain>
          }
          unit={customer.completeness == null ? "" : "%"}
          valueColor={customer.completeness == null ? c.placeholder : c.tealInk2}
        />
        <KpiCard
          label="Knowledge Atoms"
          value={<Explain snapshot={{ label: "Knowledge atoms", value: atoms }}>{atoms}</Explain>}
        />
        <KpiCard label="Owner / CSM" value={customer.csm ?? "—"} valueColor={customer.csm ? c.ink : c.placeholder} valueSize={customer.csm ? 22 : 28} />
        <KpiCard label="Tier" value={customer.tier ?? "—"} valueColor={customer.tier ? c.ink : c.placeholder} valueSize={customer.tier ? 22 : 28} />
        <KpiCard
          label="Tickets"
          value={
            ticketCount == null ? (
              "—"
            ) : (
              <Explain snapshot={{ label: "Tickets", value: ticketCount, unit: "tickets" }} prompt={`What are the themes in ${customer.name}'s tickets, and how is the support relationship trending? Cite tickets.`}>
                {ticketCount}
              </Explain>
            )
          }
          valueColor={ticketCount == null ? c.placeholder : c.ink}
        />
      </Box>

      {/* tab rail */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => goTab(v as TabKey)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: "28px" }}
      >
        {tabDefs.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} disableRipple />
        ))}
      </Tabs>

      {/* tab body */}
      {activeTab === "overview" && (
        <CustomerOverviewTabMui
          customer={customer}
          draft={draft}
          setDraft={setDraft}
          editing={editing}
          onSave={onSaveRecord}
          onCancel={() => {
            setDraft(customer);
            setEditing(false);
          }}
          isAdmin={isAdmin}
          activity={activity}
          projects={projects}
          brief={brief}
          spine={spine}
          tickets={tickets}
        />
      )}
      {activeTab === "docs" && (
        <ScopeDocsTab
          prefix={`customers/${customerId}/`}
          label={customer.name}
          openKey={searchParams.get("doc")}
          onOpenKey={(rel) => {
            const next = new URLSearchParams(searchParams);
            if (rel) next.set("doc", rel);
            else next.delete("doc");
            setSearchParams(next, { replace: true });
          }}
        />
      )}
      {activeTab === "files" && <CustomerFilesTabMui customerId={customerId} projects={projects} />}
      {activeTab === "projects" && <CustomerProjectsTabMui projects={projects} />}
      {activeTab === "engagement" && <CustomerEngagementTabMui customerId={customerId} />}
      {activeTab === "log" && <CustomerLogTabMui customerId={customerId} />}
      {activeTab === "integrations" && <CustomerIntegrationsTabMui customerId={customerId} isAdmin={isAdmin} />}
      {activeTab === "visits" && <CustomerVisitsTabMui customerId={customerId} />}
      {activeTab === "tickets" && <CustomerTicketsTabMui customerId={customerId} tickets={tickets} isAdmin={isAdmin} />}
      {activeTab === "roadmap" && <CustomerRoadmapTabMui customerId={customerId} />}
      {activeTab === "finance" && canSeeFinance && <CustomerFinanceTabMui customerId={customerId} />}

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {customer.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13.5 }}>
            This permanently removes the customer record. Files, tickets, and visits are not deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} sx={{ color: c.ink3 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setDeleteOpen(false);
              void onDelete();
            }}
            sx={{ background: c.dangerText2, "&:hover": { background: "#b53535" } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
